import React, { useState, useCallback } from 'react';
import VideoRecorder from './utils/VideoRecorder';

/**
 * VideoAnswerQuestion - Wrapper for VideoRecorder
 * 
 * Key features:
 * - Memoized to prevent unnecessary re-renders from parent
 * - Implements key-based remounting for "Record Again" functionality
 * - Detects when user clicks "Record Again" (blob URL → empty string)
 *   and forces VideoRecorder to completely remount with fresh camera stream
 */
const VideoAnswerQuestion = React.memo(function VideoAnswerQuestion({ question, value = '', onChange, disabled = false }) {
  const [recorderKey, setRecorderKey] = useState(0);
  const [isRecorderActive, setIsRecorderActive] = useState(false);
  
  // Wrap onChange to detect "Record Again" and trigger remount
  // Memoized to prevent VideoRecorder from receiving new function reference on each render
  const handleChange = useCallback((newValue) => {
    if (value && value.startsWith('blob:') && newValue === '') {
      // User clicked "Record Again" - increment key to force VideoRecorder remount
      setRecorderKey(prev => prev + 1);
      setIsRecorderActive(false);
    }
    onChange(newValue);
  }, [value, onChange]);
  
  return (
    <div>
      {!isRecorderActive ? (
        // Show "Enable Camera" button before activating camera
        <div className="w-full">
          <div className="rounded-lg border border-gray-300 bg-gray-50 p-8 flex flex-col items-center justify-center" style={{ minHeight: '360px' }}>
            <svg className="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-600 mb-4 text-center">Click below to enable your camera for recording your answer</p>
            <button
              type="button"
              onClick={() => setIsRecorderActive(true)}
              disabled={disabled}
              className={`px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors duration-200 shadow-md flex items-center ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <circle cx="12" cy="12" r="4" fill="currentColor" />
              </svg>
              Enable Camera
            </button>
          </div>
        </div>
      ) : (
        // Show VideoRecorder after button click
        <VideoRecorder 
          key={recorderKey}
          value={value} 
          onChange={handleChange} 
          disabled={disabled}
          maxRecordingTime={question.max_duration || 120}
        />
      )}
    </div>
  );
});

export default VideoAnswerQuestion;
